import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faCopy, faCheck, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import DataLoader from "../components/globalView/DataLoader";
import {
  getWebhookConfig,
  saveWebhookConfig,
  testWebhook,
  handleLoadState
} from "../store/actions";

const isValidUrl = (str) => {
  try {
    const url = new URL(str);
    return url.protocol === 'https:';
  } catch {
    return false;
  }
};

const WEBHOOK_EVENTS = [
  { name: "awaiting_deposit", cls: "wait", desc: "New swap created" },
  { name: "swapping", cls: "proc", desc: "Deposit detected, swap in progress" },
  { name: "complete", cls: "ok", desc: "Swap completed, funds sent to recipient" },
  { name: "failed", cls: "fail", desc: "Swap failed or expired" },
  { name: "refund", cls: "ref", desc: "Funds refunded to sender" },
];

const Webhooks = () => {
  const dispatch = useDispatch();
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookDisplayFees, setWebhookDisplayFees] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [secretCopied, setSecretCopied] = useState(false);

  const isLoading = useSelector((state) => state.userReducer.loading);
  const webhook = useSelector((state) => state.userReducer.webhookConfig || {});

  useEffect(() => {
    dispatch(handleLoadState(true));
    dispatch(getWebhookConfig()).finally(() => dispatch(handleLoadState(false)));
  }, [dispatch]);

  useEffect(() => {
    if (webhook) {
      setWebhookUrl(webhook.webhookUrl || '');
      setWebhookEnabled(webhook.webhookEnabled || false);
      setWebhookDisplayFees(webhook.webhookDisplayFees || false);
      setWebhookSecret(webhook.webhookSecret || '');
    }
  }, [webhook]);

  const handleSave = () => {
    setError('');
    setSuccessMessage('');

    if (webhookEnabled && !webhookUrl) {
      setError('Please enter a webhook URL.');
      return;
    }

    if (webhookUrl && !isValidUrl(webhookUrl)) {
      setError('Webhook URL must be a valid HTTPS URL.');
      return;
    }

    setIsSaving(true);
    dispatch(saveWebhookConfig({ webhookUrl, webhookEnabled, webhookDisplayFees }))
      .then((res) => {
        setSuccessMessage('Webhook configuration saved successfully.');
        if (res?.webhookSecret) {
          setWebhookSecret(res.webhookSecret);
        }
        setTimeout(() => setSuccessMessage(''), 3000);
      })
      .catch(() => {
        setError('Failed to save webhook configuration. Please try again.');
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleTest = () => {
    if (!webhookUrl || !isValidUrl(webhookUrl)) {
      setError('Please save a valid webhook URL first.');
      return;
    }
    setIsTesting(true);
    setTestResult(null);
    setError('');
    dispatch(testWebhook())
      .then((result) => {
        setTestResult(result);
      })
      .catch(() => {
        setTestResult({ success: false, message: 'Test request failed.' });
      })
      .finally(() => {
        setIsTesting(false);
      });
  };

  const copySecret = () => {
    navigator.clipboard.writeText(webhookSecret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  if (isLoading) {
    return <DataLoader />;
  }

  return (
    <>
      <Helmet>
        <title>Webhook Settings | Instaswap Partners</title>
      </Helmet>
      <div>
        <div className="secHdr">
          <h2 className="secTitle">Webhook Notifications</h2>
          <p className="secLead">
            Receive real-time HTTP POST notifications whenever a swap changes
            state, so your systems stay in sync without polling.
          </p>
        </div>

        <div className="panel" style={{ maxWidth: 760, padding: 24 }}>
          {/* Enable toggle */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 20,
              marginBottom: 20,
              borderBottom: "1px solid rgba(255,255,255,.06)",
            }}
          >
            <span className="fldLbl" style={{ margin: 0 }}>Enable webhooks</span>
            <button
              type="button"
              className={`whToggle${webhookEnabled ? " on" : ""}`}
              onClick={() => setWebhookEnabled(!webhookEnabled)}
              aria-label="Enable webhooks"
            />
          </div>

          {/* Include fee breakdown in webhook payloads */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 20,
              marginBottom: 20,
              borderBottom: "1px solid rgba(255,255,255,.06)",
            }}
          >
            <div>
              <span className="fldLbl" style={{ margin: 0 }}>Include fee breakdown</span>
              <div className="fldHelp" style={{ marginTop: 4 }}>
                Adds a feeBreakdown array (instaswap / affiliate / subAffiliate, USD) to every webhook payload.
              </div>
            </div>
            <button
              type="button"
              className={`whToggle${webhookDisplayFees ? " on" : ""}`}
              onClick={() => setWebhookDisplayFees(!webhookDisplayFees)}
              disabled={!webhookEnabled}
              aria-label="Include fee breakdown in webhooks"
            />
          </div>

          {/* Webhook URL */}
          <div className="fld">
            <label className="fldLbl">Webhook URL</label>
            <input
              className="input"
              type="text"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://your-server.com/webhook"
              disabled={!webhookEnabled}
            />
            <div className="fldHelp">
              Must be HTTPS. We'll POST swap state changes to this URL.
            </div>
          </div>

          {/* Webhook Secret (read-only, auto-generated) */}
          {webhookSecret && (
            <div className="fld">
              <label className="fldLbl">Signing secret</label>
              <div style={{ display: "flex", gap: 10 }}>
                <input
                  className="input mono"
                  type="text"
                  value={webhookSecret}
                  readOnly
                />
                <button
                  type="button"
                  className="iconBtn"
                  style={{ width: 48, height: 48, borderRadius: 11 }}
                  onClick={copySecret}
                  title="Copy secret"
                >
                  <FontAwesomeIcon icon={secretCopied ? faCheck : faCopy} />
                </button>
              </div>
              <div className="fldHelp">
                Verify signatures with header{" "}
                <code>X-Instaswap-Signature: sha256=hmac(secret, body)</code>
                {secretCopied ? " · Copied to clipboard." : ""}
              </div>
            </div>
          )}

          {/* Payload example */}
          <div className="fld">
            <label className="fldLbl">Example payload</label>
            <pre className="codeBox">
{`{
  "swap_id": "abc123def456",
  "affiliate_id": "YOUR_ID",
  "status": "complete",
  "from_currency": "ETH",
  "to_currency": "XMR",
  "amount": 1.5,
  "actual_output": 12.345,
  "timestamp": "2026-04-04T12:00:00.000Z"
}`}
            </pre>
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", gap: 12 }}>
            <button
              type="button"
              className="btnPrimary"
              style={{ flex: 1, height: 48 }}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? <FontAwesomeIcon icon={faSpinner} spin /> : "Save configuration"}
            </button>
            <button
              type="button"
              className="btnGhost"
              style={{ height: 48 }}
              onClick={handleTest}
              disabled={isTesting || !webhookUrl || !webhookEnabled}
              title="Send a test webhook"
            >
              {isTesting ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} />
                  Test
                </>
              )}
            </button>
          </div>

          {/* Messages */}
          {error && (
            <div className="notice red" style={{ marginTop: 16 }}>
              <span className="ni">⚠</span>
              <div>{error}</div>
            </div>
          )}
          {successMessage && (
            <div className="notice green" style={{ marginTop: 16 }}>
              <span className="ni">✓</span>
              <div>{successMessage}</div>
            </div>
          )}

          {/* Test result */}
          {testResult && (
            <div
              className={`notice ${testResult.success ? "green" : "red"}`}
              style={{ marginTop: 16 }}
            >
              <span className="ni">{testResult.success ? "✓" : "✕"}</span>
              <div>
                {testResult.message ||
                  (testResult.success
                    ? "Webhook delivered successfully!"
                    : "Webhook delivery failed.")}
                {testResult.statusCode && (
                  <span style={{ color: "#7c847e", marginLeft: 8 }}>
                    (HTTP {testResult.statusCode})
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Events info */}
          <div
            style={{
              marginTop: 24,
              paddingTop: 20,
              borderTop: "1px solid rgba(255,255,255,.06)",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: 13,
                color: "#cdd5cf",
                marginBottom: 14,
              }}
            >
              Webhooks are sent on the following events
            </div>
            {WEBHOOK_EVENTS.map((ev) => (
              <div className="whEv" key={ev.name}>
                <span className={`whEvName ${ev.cls}`}>{ev.name}</span>
                <span className="whEvDesc">{ev.desc}</span>
              </div>
            ))}
            <div style={{ fontSize: 12, color: "#7c847e", marginTop: 16 }}>
              Retry policy: 3 attempts with exponential backoff. Timeout 10s per
              attempt.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Webhooks;
